/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Container from '../common/components/Container/Container';
import Button, { ButtonStyle } from '../common/components/Button/Button';
import ButtonContainer from '../common/components/Button/ButtonContainer';
import TitleContainer from '../common/components/TitleContainer/TitleContainer';
import withRouteAnalytics from '../services/analytics/withRouteAnalytics';
import { Screens } from '../services/analytics/screens';
import { useCredentialStore } from '../providers/CredentialStoreContext';
import { useValidation } from '../providers/ValidationContext';
import { Routes } from '../config/routes';
import { useGKNService } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import Loading from '../common/components/Loading/Loading';
import { useScopeRequest } from '../providers/ScopeRequestContext';
import Log from '../common/logging';
import { DecryptionStatus, ReuseStatus } from '../common/types';

function CheckForExistingCredentialsScreen(): JSX.Element | null {
  const { supportsCredentialReuse } = useGKNService();
  const { scopeRequest } = useScopeRequest();
  const { getRouteForCurrentStatus } = useValidation();
  const {
    storeStatus,
    retrieveExistingInProgress,
    allCredentialsStored,
    decryptStatus,
    decryptCredentials,
    getExistingRequiredCredentialIds,
    setDecryptStatus,
    setReuseStatus,
    decryptedCredentials,
  } = useCredentialStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isReusePossible, setIsReusePossible] = useState<boolean>();

  // this is the success case where we have decrypted credentials in the store
  useEffect(() => {
    if (decryptedCredentials) {
      navigate(Routes.CHOOSE_CREDENTIALS);
    }
  }, [decryptedCredentials, navigate]);

  const onConfirm = useCallback(async () => {
    if (isReusePossible && decryptStatus !== DecryptionStatus.inProgress) {
      try {
        const credentialsToDecrypt = await getExistingRequiredCredentialIds();
        if (!credentialsToDecrypt) {
          setDecryptStatus(DecryptionStatus.failed);
          return;
        }
        decryptCredentials(credentialsToDecrypt);
      } catch (error) {
        setDecryptStatus(DecryptionStatus.failed);
      }
    }
  }, [isReusePossible, decryptStatus, getExistingRequiredCredentialIds, decryptCredentials, setDecryptStatus]);

  useEffect(() => {
    setIsReusePossible(undefined);
    if (!scopeRequest || retrieveExistingInProgress) {
      return;
    }
    allCredentialsStored().then((areAllCredentialsStored) => {
      setIsReusePossible(areAllCredentialsStored);
    });
  }, [retrieveExistingInProgress, allCredentialsStored, scopeRequest]);

  useEffect(() => {
    const abortController = new AbortController();

    if (!supportsCredentialReuse) {
      return;
    }
    // we're still waiting on the existing credentials call to complete
    if (isReusePossible === undefined) {
      return;
    }

    if (!isReusePossible) {
      Log.debug('Credential reuse not possible navigate(Routes.VALIDATION_NEXT_STEP)');
      setReuseStatus(ReuseStatus.notEligible);
      navigate(Routes.VALIDATION_NEXT_STEP);
      return;
    }
    // eslint-disable-next-line consistent-return
    return () => {
      abortController.abort();
    };
  }, [
    isReusePossible,
    navigate,
    storeStatus,
    retrieveExistingInProgress,
    scopeRequest,
    getRouteForCurrentStatus,
    allCredentialsStored,
    setReuseStatus,
    supportsCredentialReuse,
  ]);

  const isInProgress = useMemo(() => {
    if (decryptStatus) {
      return [DecryptionStatus.inProgress, DecryptionStatus.confirmed].includes(decryptStatus);
    }
    return false;
  }, [decryptStatus]);

  const onConfirmUseAlternative = useCallback(async () => {
    setReuseStatus(ReuseStatus.rejected);
    navigate(Routes.VALIDATION_NEXT_STEP);
  }, [navigate, setReuseStatus]);

  return (
    <Container>
      {retrieveExistingInProgress || isReusePossible === undefined ? (
        <div className="mb-8">
          <Loading />
        </div>
      ) : (
        <>
          <TitleContainer center>
            <h1 className="font-normal mb-4" data-testid="DECRYPT_CREDENTIAL_PROMPT">
              <span>🔒 </span>
              {t('screens.credentialDecrypt.title')}
            </h1>
            <div data-testid="DECRYPT_STATUS">
              {decryptStatus === DecryptionStatus.failed ? (
                <div>{t('screens.credentialDecrypt.error')}</div>
              ) : (
                <div>{t('screens.credentialDecrypt.description')}</div>
              )}
            </div>
          </TitleContainer>
          <ButtonContainer bottomMargin="mb-2 mt-4">
            <Button
              style={ButtonStyle.DEFAULT}
              disabled={isInProgress}
              loading={isInProgress}
              dataTestid="CONFIRM_DECRYPT_BUTTON"
              onClick={() => onConfirm()}
            >
              <span>{t('screens.credentialDecrypt.continueButton')}</span>
            </Button>
          </ButtonContainer>
          <ButtonContainer bottomMargin="mb-2 mt-4">
            <Button
              style={ButtonStyle.BACK}
              disabled={false}
              loading={false}
              dataTestid="CONFIRM_USE_ALTERNATIVE_CREDENTIALS_BUTTON"
              onClick={() => onConfirmUseAlternative()}
            >
              <span>{t('screens.chooseCredentials.useAlternativeCredentialsButton')}</span>
            </Button>
          </ButtonContainer>
        </>
      )}
    </Container>
  );
}
export default withRouteAnalytics(CheckForExistingCredentialsScreen, Screens.CHECK_FOR_EXISTING_CREDENTIALS);
